import React from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import Filters from "components/Common/Filters"
import { pageSizes } from "components/Common/constants"
import Switch from "components/Shared/Switch"
import { useState } from "react"
import { useEffect } from "react"
import {
  deleteCountry,
  getAllCountries,
  updateCountryStatus,
} from "store/actions/countriesAction"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { showAlert } from "store/reducers/alertReducer"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import isPermissionEnabled from "common/isPermissionEnabled"
import TableShimmer from "components/Shared/TableShimmer"
import DeleteModal from "components/Common/DeleteModal"
import { useTranslation } from "react-i18next"

const Countries = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Countries List | Sign Burger")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })
  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    countries,
  } = useSelector(state => state.Countries)

  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { userRole, accountType } = useSelector(state => state.Login)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      11,
      "country_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      11,
      "country_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      11,
      "country_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      11,
      "country_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    getCountries()
  }, [])

  const getCountries = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(getAllCountries(payload))
    setIsApiCalled(true)
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Enabled"), value: 1 },
        { label: t("Disabled"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const handleFetchCountries = (page = 1) => {
    const { status, sortOrder } = selectedFilters

    // Initialize searchParams object
    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    // Create payload for dispatch
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    // Dispatch action and navigate on success
    dispatch(getAllCountries(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/countries?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchCountries(1)
  }

  const onPageChange = page => {
    handleFetchCountries(page)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllCountries(payload))
    navigate(`/countries?page=${1}`)
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
  }

  const handleCountryStatus = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    dispatch(updateCountryStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteCountry(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  cardTitle={t("Countries List")}
                  isAddBtn={loading ? false : usersPermissions.create}
                  addBtnText={t("Add Country")}
                  onAddBtnClick={() => navigate("/countries/add")}
                />
                <CardBody>
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    isCustomPageSize={true}
                    pageSizes={pageSizes}
                    itemsPerPage={10}
                    setItemsPerPage={10}
                    searchBy={t("Name, Phone Code, Country Code")}
                  />

                  <div className="">
                    {loading ? (
                      <TableShimmer row={7} col={5} />
                    ) : countries.length === 0 ? (
                      <p className="text-center my-2">
                        {t("No countries found")}
                      </p>
                    ) : (
                      <Table className="table mb-0" hover>
                        <thead className="table-light">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th>{t("Name")}</th>
                            <th>{t("Phone Code")}</th>
                            <th>{t("Country Code")}</th>
                            <th>{t("Currency")}</th>
                            <th>{t("VAT %")}</th>
                            <th>{t("Status")}</th>
                            <th>{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {countries.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(
                                  `/countries/country-details?id=${item._id}`
                                )
                              }}
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {" "}
                                {(currentPage - 1) * 10 + index + 1}
                              </th>
                              <td>
                                {language === "sa" ? item.nameAr : item.name}
                              </td>
                              <td>{item.dialCode}</td>
                              <td>{item.countryCode}</td>
                              <td>{item.currency}</td>
                              <td>{item?.taxInfo?.value}</td>
                              <td>
                                {item.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Enabled")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Disabled")}
                                  </Badge>
                                )}
                              </td>

                              <td className="d-flex align-items-center gap-2">
                                {usersPermissions.edit && (
                                  <Switch
                                    onClickHandler={e => {
                                      e.stopPropagation()
                                      handleCountryStatus(item._id, item.status)
                                    }}
                                    isChecked={item.status === 1}
                                    disabled={
                                      item._id === selectedId && isLoading
                                    }
                                  />
                                )}
                                <UncontrolledDropdown>
                                  <DropdownToggle
                                    tag="a"
                                    className="card-drop"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <i className="mdi mdi-dots-vertical font-size-18"></i>
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                    {usersPermissions.edit && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/countries/edit?id=${item._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                        {t("Edit")}
                                      </DropdownItem>
                                    )}
                                    <DropdownItem
                                      onClick={e => {
                                        e.stopPropagation()
                                        navigate(
                                          `/countries/country-details?id=${item._id}`
                                        )
                                      }}
                                    >
                                      <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                      {t("View")}
                                    </DropdownItem>
                                    {usersPermissions.delete && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          setItemIdToDelete(item._id)
                                          setShowDeleteModal(true)
                                        }}
                                      >
                                        <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                        {t("Delete")}
                                      </DropdownItem>
                                    )}
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                  {!loading && countries.length > 0 && (
                    <div className="mt-3">
                      <Paginations
                        data={countries}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText="Delete"
        bodyTitle="Are you sure you want to delete this country?"
      />
    </React.Fragment>
  )
}

export default Countries
